import React, { useRef } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ColumnLayout } from "@/libraries/design/foundations/grid/src/column-layout";
import wacToken from "@/libraries/design/foundations/token/src/wac-token";
import { useDarkTheme } from "@/libraries/design/utils/dark-theme-provider";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src/data-event-props";

import { GlobalNavProvider } from "./nav-links-container";
import SearchAndProfile from "./nav-search-and-profile";
import { SkipToContent } from "./skip-to-content";

export type SimplifiedProductSubNavProps = {
    readonly productLogo: JSX.Element;
    readonly productLogoUrl: string;
    readonly productLogoAriaLabel: string;
    readonly productKey?: ProductKeyTypes;
    readonly isShowTryNowLink?: boolean;
    readonly isShowUserProfile?: boolean;
    readonly navigationTrynowCtaText?: string;
    readonly navigationTrynowCtaUrl?: string;
    readonly navigationTrynowCtaOnClick?: (e: React.MouseEvent) => void;
    readonly navigationTrynowCtaButtonVariant?: "primary" | "secondary";
};

const simplifiedProductPageSubNavStyle = css({
    position: "sticky",
    insetBlockStart: 0,
    zIndex: 5,
    width: "100%",
    minHeight: token("space.800"),
    backgroundColor: wacToken("color.neutral.light0")
});

const simplifiedProductPageSubNavDarkThemeStyle = css({
    "backgroundColor": wacToken("color.neutral.dark100"),

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "button[name='sign-in'] span": {
        color: wacToken("color.neutral.light0")
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".avatar-button span": {
        color: wacToken("color.neutral.light0")
    }
});

const simplifiedProductPageSubNavContentStyle = css({
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: token("space.300"),
    width: "100%",
    minHeight: token("space.800"),
    marginInline: "auto",
    [media.below.sm]: {
        gap: token("space.150")
    }
});

const simplifiedProductPageSubNavLogoLinkStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
    textDecoration: "none"
});

const simplifiedProductPageSubNavActionsStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexShrink: 0
});

export const SimplifiedProductSubNav = ({
    productLogo,
    productLogoUrl,
    productLogoAriaLabel,
    productKey,
    isShowTryNowLink = true,
    isShowUserProfile = true,
    navigationTrynowCtaText,
    navigationTrynowCtaUrl = "/try",
    navigationTrynowCtaOnClick,
    navigationTrynowCtaButtonVariant = "primary"
}: SimplifiedProductSubNavProps) => {
    const intl = useIntl();
    const { isDarkTheme } = useDarkTheme();
    const [, setIsOpenSearch] = React.useState(false);
    const searchAndProfileRef = useRef<HTMLDivElement | null>(null);

    const defaultCtaText = intl.formatMessage({
        id: "productpagesubnav.try-now-button-label",
        defaultMessage: "Get it free"
    });

    const themedProductLogo =
        React.isValidElement<{ lightMode?: boolean }>(productLogo) &&
        typeof productLogo.type !== "string"
            ? React.cloneElement(productLogo, { lightMode: isDarkTheme })
            : productLogo;

    return (
        <GlobalNavProvider globalNavItemKeys={[]}>
            <header
                className="simplified-product-nav-container"
                css={[
                    simplifiedProductPageSubNavStyle,
                    isDarkTheme && simplifiedProductPageSubNavDarkThemeStyle
                ]}
                data-testid="simplified-product-subnav"
            >
                <ColumnLayout layout="12">
                    <nav
                        aria-label={intl.formatMessage({
                            id: "productPageNav.main-nav-label",
                            defaultMessage: "Main"
                        })}
                        css={simplifiedProductPageSubNavContentStyle}
                    >
                        <SkipToContent />
                        <a
                            className="navigation-bar-logo-link"
                            css={simplifiedProductPageSubNavLogoLinkStyle}
                            href={productLogoUrl}
                            aria-label={productLogoAriaLabel}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="productPageNav"
                            data-product-key={productKey}
                        >
                            {themedProductLogo}
                        </a>
                        <div css={simplifiedProductPageSubNavActionsStyle}>
                            <SearchAndProfile
                                isShowTryNowLink={isShowTryNowLink}
                                productKey={productKey}
                                isShowSearchButton={false}
                                setIsOpenSearch={setIsOpenSearch}
                                navigationTrynowCtaText={
                                    navigationTrynowCtaText ?? defaultCtaText
                                }
                                navigationTrynowCtaUrl={navigationTrynowCtaUrl}
                                navigationTrynowCtaOnClick={
                                    navigationTrynowCtaOnClick
                                }
                                navigationTrynowCtaButtonVariant={
                                    navigationTrynowCtaButtonVariant
                                }
                                isDisplayDivider={isShowTryNowLink}
                                searchAndProfileRef={searchAndProfileRef}
                                isShowAppSwitcher={false}
                                isShowUserProfile={isShowUserProfile}
                                navType="productNav"
                            />
                        </div>
                    </nav>
                </ColumnLayout>
            </header>
        </GlobalNavProvider>
    );
};
